import React, { useState, useEffect, useRef } from "react";
import { Table, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadFile,
  downloadFile,
  deleteFile,
} from "../Redux/Actions/FileManagementAction";
import { S_ACCOUNT_ID } from "../Communication/Constants";
import {
  getFileIconFromName,
  getFileTypeFromName,
  formatFileDateTime,
} from "../Utils/FileExtensionHelpers";

export default function MembersDocument({
  selectedMember,
  doFileList,
  doUploadFile,
}) {
  // Redux dispatch
  const dispatch = useDispatch();

  // File management state from Redux
  const fileManagement = useSelector((state) => state.FileManagementReducer);
  const {
    files,
    totalFiles,
    totalPages: fileTotalPages,
    currentPage: fileCurrentPage,
    isUploading,
    uploadProgress,
    isLoading: fileLoading,
    error: fileError,
    success: fileSuccess,
    stats: fileStats,
  } = fileManagement;

  // File management local state
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [selectedFileForUpload, setSelectedFileForUpload] = useState(null);
  const [fileDescription, setFileDescription] = useState("");
  const [fileTypeForUpload, setFileTypeForUpload] = useState("");
  const [filePage, setFilePage] = useState(0);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'

  // File viewer modal state
  const [fileViewerModalOpen, setFileViewerModalOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [fileViewerUrl, setFileViewerUrl] = useState(null);

  // File sorting state
  const [fileSortConfig, setFileSortConfig] = useState({
    sortBy: "uploadedAt",
    sortDirection: "DESC",
  });

  // File pagination state
  const [filePagination, setFilePagination] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });

  // Hidden file input ref for direct file selection
  const fileInputRef = useRef(null);

  // Track if component has mounted to trigger initial load
  const hasMountedRef = useRef(false);

  // Initial load effect - runs when component becomes visible or selectedMember changes
  useEffect(() => {
    console.log("MembersDocument: Component mounted/visible", selectedMember);

    if (selectedMember?.id) {
      console.log("MembersDocument: Initial load - fetching file list");
      doFileList({
        accountId: selectedMember.accountId,
        page: 0,
        size: 20,
        fileType: null,
      });
      hasMountedRef.current = true;
    }
  }, [selectedMember?.accountId, doFileList]); // Trigger when member changes or component mounts

  // File management functions - triggers when dependencies change
  useEffect(() => {
    // Skip the first run since initial load effect handles it
    if (!hasMountedRef.current) {
      return;
    }

    if (selectedMember?.accountId) {
      console.log("MembersDocument: Dependency change - loading file list", {
        accountId: selectedMember.accountId,
        page: filePage,
        fileType: selectedFileType,
      });

      doFileList({
        accountId: selectedMember.accountId,
        page: filePage,
        size: 20,
        fileType: selectedFileType,
      });
      //dispatch(getFileStats(selectedMember.accountId));
    }
  }, [
    doFileList,
    selectedMember?.accountId,
    filePage,
    selectedFileType,
    fileSuccess,
  ]);

  const handleFileUpload = (file, fileType, description) => {
    if (selectedMember?.accountId && file) {
      doUploadFile(
        selectedMember.accountId,
        file,
        fileType,
        description,
        () => {
          // Re-query file list after successful upload
          doFileList({
            accountId: selectedMember.accountId,
            page: filePage,
            size: 20,
            fileType: selectedFileType,
          });
        }
      );
      setFileUploadModalOpen(false);
      setSelectedFileForUpload(null);
      setFileDescription("");
      setFileTypeForUpload("");
    }
  };

  const handleFileView = (file) => {
    dispatch(
      downloadFile(file.id, (response) => {
        if (response && response.data) {
          // Determine the correct MIME type based on file extension
          const extension = file.fileName?.split(".").pop()?.toLowerCase();
          let mimeType =
            response.headers["content-type"] || "application/octet-stream";

          // Override MIME type if needed based on file extension
          if (extension === "pdf" && !mimeType.includes("pdf")) {
            mimeType = "application/pdf";
          } else if (
            ["jpg", "jpeg"].includes(extension) &&
            !mimeType.includes("image")
          ) {
            mimeType = "image/jpeg";
          } else if (extension === "png" && !mimeType.includes("image")) {
            mimeType = "image/png";
          } else if (extension === "gif" && !mimeType.includes("image")) {
            mimeType = "image/gif";
          } else if (extension === "bmp" && !mimeType.includes("image")) {
            mimeType = "image/bmp";
          }

          // Create a blob URL from the response data with correct MIME type
          const blob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(blob);

          // Open the file viewer modal
          setViewingFile(file);
          setFileViewerUrl(url);
          setFileViewerModalOpen(true);
        }
      })
    );
  };

  const handleFileDownload = (fileId, fileName) => {
    dispatch(
      downloadFile(fileId, (response) => {
        if (response && response.data) {
          // Create a blob URL from the response data
          const blob = new Blob([response.data], {
            type:
              response.headers["content-type"] || "application/octet-stream",
          });
          const url = window.URL.createObjectURL(blob);

          // Create a temporary link element to trigger download
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName || "download";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the blob URL
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        }
      })
    );
  };

  const handlePrintFile = () => {
    if (fileViewerUrl) {
      // Create a hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = fileViewerUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
    }
  };

  const closeFileViewer = () => {
    if (fileViewerUrl) {
      window.URL.revokeObjectURL(fileViewerUrl);
    }
    setFileViewerModalOpen(false);
    setViewingFile(null);
    setFileViewerUrl(null);
  };

  const handleFileDelete = (fileId) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      dispatch(deleteFile(fileId));
    }
  };

  const handleFileTypeFilter = (fileType) => {
    setSelectedFileType(fileType);
    setFilePage(0);
  };

  // File sort handler function
  const handleFileSort = (field) => {
    const newDirection =
      fileSortConfig.sortBy === field && fileSortConfig.sortDirection === "ASC"
        ? "DESC"
        : "ASC";

    setFileSortConfig({
      sortBy: field,
      sortDirection: newDirection,
    });

    // Reset to first page when sorting
    setFilePagination((prev) => ({ ...prev, currentPage: 0 }));
    setFilePage(0);

    // Trigger API call with new sort parameters
    if (selectedMember?.accountId) {
      doFileList({
        accountId: selectedMember.accountId,
        page: 0,
        size: 20,
        fileType: selectedFileType,
        sortBy: field,
        sortDirection: newDirection,
      });
    }
  };

  // File sortable header component
  const FileSortableHeader = ({ field, label }) => {
    const getSortIcon = () => {
      if (fileSortConfig.sortBy !== field) {
        return (
          <i
            className="fas fa-sort"
            style={{ fontSize: "10px", marginLeft: "4px" }}
          ></i>
        );
      }
      return fileSortConfig.sortDirection === "ASC" ? "▲" : "▼";
    };

    return (
      <th
        onClick={() => handleFileSort(field)}
        style={{
          cursor: "pointer",
          userSelect: "none",
          position: "relative",
        }}
        className="sortable-header"
      >
        {label}
        <span style={{ marginLeft: "5px", fontSize: "0.8em", color: "#666" }}>
          {getSortIcon()}
        </span>
      </th>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatUploadDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30)
      return `${Math.ceil(diffDays / 7)} week${
        Math.ceil(diffDays / 7) > 1 ? "s" : ""
      } ago`;
    if (diffDays < 365)
      return `${Math.ceil(diffDays / 30)} month${
        Math.ceil(diffDays / 30) > 1 ? "s" : ""
      } ago`;
    return date.toLocaleDateString();
  };

  return (
    <div>
      {/* File Viewer Modal */}
      {fileViewerModalOpen && viewingFile && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1050 }}
          onClick={closeFileViewer}
        >
          <div
            className="modal-dialog modal-xl"
            style={{ maxWidth: "90%", height: "90vh", margin: "2.5vh auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="modal-header"
                style={{ borderBottom: "1px solid #dee2e6" }}
              >
                <h5 className="modal-title">
                  <i className="fas fa-file-alt mr-2"></i>
                  {viewingFile.originalFileName || viewingFile.fileName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeFileViewer}
                ></button>
              </div>
              <div
                className="modal-body p-0"
                style={{
                  flex: 1,
                  overflow: "auto",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {fileViewerUrl && (
                  <>
                    {viewingFile.fileType === "PDF" ||
                    viewingFile.fileName?.toLowerCase().endsWith(".pdf") ||
                    viewingFile.originalFileName
                      ?.toLowerCase()
                      .endsWith(".pdf") ? (
                      <iframe
                        src={`${fileViewerUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                        }}
                        title="PDF Viewer"
                      />
                    ) : viewingFile.fileType === "IMAGE" ||
                      ["jpg", "jpeg", "png", "gif", "bmp"].includes(
                        viewingFile.fileName?.split(".").pop()?.toLowerCase()
                      ) ||
                      ["jpg", "jpeg", "png", "gif", "bmp"].includes(
                        viewingFile.originalFileName
                          ?.split(".")
                          .pop()
                          ?.toLowerCase()
                      ) ? (
                      <img
                        src={fileViewerUrl}
                        alt={
                          viewingFile.originalFileName || viewingFile.fileName
                        }
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          console.error("Image failed to load:", e);
                        }}
                      />
                    ) : viewingFile.fileType === "VIDEO" ||
                      ["mp4", "avi", "mov", "wmv"].includes(
                        viewingFile.fileName?.split(".").pop()?.toLowerCase()
                      ) ? (
                      <video
                        controls
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                        }}
                      >
                        <source src={fileViewerUrl} />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="text-center p-5">
                        <i
                          className="fas fa-file-alt"
                          style={{
                            fontSize: "4rem",
                            color: "#6c757d",
                            marginBottom: "1rem",
                          }}
                        ></i>
                        <p className="text-muted">
                          Preview not available for this file type.
                          <br />
                          Please download the file to view it.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div
                className="modal-footer"
                style={{
                  borderTop: "1px solid #dee2e6",
                  justifyContent: "left",
                  gap: "0.5rem",
                  padding: "1rem",
                }}
              >
                <button
                  type="button"
                  className="[ btn btn-sm ] [ tertiary-btn ]"
                  onClick={handlePrintFile}
                  style={{ paddingRight: "0px", borderRadius: "0.5rem" }}
                >
                  <span className="fas fa-print"></span>
                </button>
                <button
                  type="button"
                  className="[ btn btn-sm ] [ tertiary-btn ]"
                  onClick={() =>
                    handleFileDownload(
                      viewingFile.id,
                      viewingFile.originalFileName || viewingFile.fileName
                    )
                  }
                  style={{ paddingRight: "0px", borderRadius: "0.5rem" }}
                >
                  <span className="fas fa-file-download"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {fileUploadModalOpen && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload Document</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setFileUploadModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select File</label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={(e) =>
                      setSelectedFileForUpload(e.target.files[0])
                    }
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">File Type</label>
                  <select
                    className="form-control"
                    value={fileTypeForUpload}
                    onChange={(e) => setFileTypeForUpload(e.target.value)}
                  >
                    <option value="">Select file type</option>
                    <option value="PDF">PDF</option>
                    <option value="IMAGE">Image</option>
                    <option value="VIDEO">Video</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={fileDescription}
                    onChange={(e) => setFileDescription(e.target.value)}
                    placeholder="Enter a description for this file..."
                  />
                </div>
                {isUploading && (
                  <div className="mb-3">
                    <div className="progress">
                      <div
                        className="progress-bar"
                        style={{ width: `${uploadProgress}%` }}
                      >
                        {uploadProgress}%
                      </div>
                    </div>
                  </div>
                )}
                {fileError && (
                  <div className="alert alert-danger">{fileError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setFileUploadModalOpen(false)}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() =>
                    handleFileUpload(
                      selectedFileForUpload,
                      fileTypeForUpload,
                      fileDescription
                    )
                  }
                  disabled={
                    !selectedFileForUpload || !fileTypeForUpload || isUploading
                  }
                >
                  {isUploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Container Box for Uploaded Documents */}
      <div className="">
        <div className="row">
          {/* Left Column - Document Grid */}
          <div className="col-md-8">
            <div className="">
              {/* Documents Header with View Toggle and Upload Button */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <div
                    className="d-flex align-items-center"
                    style={{ padding: "5px" }}
                  >
                    <i
                      className="fas fa-folder-open mr-2"
                      style={{ fontSize: "1.2rem", paddingRight: "3px" }}
                    ></i>
                    <h6 className="fw-bold mb-0">Uploaded Documents</h6>
                  </div>
                </div>
                <div className="col-md-6 d-flex justify-content-end gap-2">
                  {/* Grid/List View Toggle */}
                  <div
                    className="btn-group"
                    role="group"
                    style={{ padding: "5px" }}
                  >
                    <button
                      type="button"
                      className={`btn btn-outline-secondary btn-sm ${
                        viewMode === "grid" ? "active" : ""
                      }`}
                      style={{ fontSize: "12px", padding: "4px 8px" }}
                      onClick={() => setViewMode("grid")}
                    >
                      <i className="fas fa-th"></i>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary btn-sm ${
                        viewMode === "list" ? "active" : ""
                      }`}
                      style={{ fontSize: "12px", padding: "4px 8px" }}
                      onClick={() => setViewMode("list")}
                    >
                      <i className="fas fa-list"></i>
                    </button>
                  </div>

                  {/* Upload New Button */}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    style={{
                      fontSize: "12px",
                      padding: "3px 10px",
                      margin: "5px 5px",
                      color: "#e91e63",
                      borderColor: "#e91e63",
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    + Upload New
                  </button>
                  {/* Hidden file input for direct file selection */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.mp4,.avi,.mov,.wmv,.tif,.txt,.csv,.xml,.json,.xls,.xlsx,.doc,.docx,.msg,.zip,.pgp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Automatically determine file type based on extension
                        const extension = file.name
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        let fileType = "OTHER";

                        if (extension === "pdf") {
                          fileType = "PDF";
                        } else if (
                          ["jpg", "jpeg", "png", "gif", "bmp", "tif"].includes(
                            extension
                          )
                        ) {
                          fileType = "IMAGE";
                        } else if (
                          ["mp4", "avi", "mov", "wmv"].includes(extension)
                        ) {
                          fileType = "VIDEO";
                        }

                        // Automatically upload the file with determined type and no description
                        dispatch(
                          uploadFile(
                            sessionStorage.getItem(S_ACCOUNT_ID),
                            file,
                            fileType,
                            "",
                            () => {
                              // Re-query file list after successful upload
                              doFileList({
                                accountId: sessionStorage.getItem(S_ACCOUNT_ID),
                                page: filePage,
                                size: 20,
                                fileType: selectedFileType,
                              });
                            }
                          )
                        );

                        // Clear the input value so the same file can be selected again if needed
                        e.target.value = "";
                      }
                    }}
                  />
                </div>
              </div>
              <div className="">
                {/* Filter Buttons */}
                <div className="mb-3 d-flex gap-2">
                  <button
                    className={`btn btn-sm ${
                      selectedFileType === null
                        ? "btn-dark"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      fontSize: "12px",
                      minWidth: "60px",
                      marginRight: "8px",
                      borderRadius: ".4rem",
                    }}
                    onClick={() => handleFileTypeFilter(null)}
                  >
                    All
                  </button>
                  <button
                    className={`btn btn-sm ${
                      selectedFileType === "PDF"
                        ? "btn-dark"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      fontSize: "12px",
                      minWidth: "60px",
                      marginRight: "8px",
                      borderRadius: ".4rem",
                    }}
                    onClick={() => handleFileTypeFilter("PDF")}
                  >
                    PDF
                  </button>
                  <button
                    className={`btn btn-sm ${
                      selectedFileType === "VIDEO"
                        ? "btn-dark"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      fontSize: "12px",
                      minWidth: "60px",
                      marginRight: "8px",
                      borderRadius: ".4rem",
                    }}
                    onClick={() => handleFileTypeFilter("VIDEO")}
                  >
                    Video
                  </button>
                  <button
                    className={`btn btn-sm ${
                      selectedFileType === "IMAGE"
                        ? "btn-dark"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      fontSize: "12px",
                      minWidth: "60px",
                      marginRight: "8px",
                      borderRadius: ".4rem",
                    }}
                    onClick={() => handleFileTypeFilter("IMAGE")}
                  >
                    Images
                  </button>
                  <button
                    className={`btn btn-sm ${
                      selectedFileType === "OTHER"
                        ? "btn-dark"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      fontSize: "12px",
                      minWidth: "60px",
                      marginRight: "8px",
                      borderRadius: ".4rem",
                    }}
                    onClick={() => handleFileTypeFilter("OTHER")}
                  >
                    Other
                  </button>
                </div>
                {/* Document Grid/List View */}
                {viewMode === "grid" ? (
                  <>
                    {fileLoading ? (
                      <div className="col-12 text-center py-4">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Loading files...
                      </div>
                    ) : files.length > 0 ? (
                      <ul className="branch-widget--uploaded-files__list">
                        {files.map((file, index) => {
                          const rawFileName =
                            file.originalFileName || file.fileName || "";

                          const fileIcon = getFileIconFromName(rawFileName);

                          const displayFileName =
                            rawFileName.length > 50
                              ? `${rawFileName.slice(0, 47)}…`
                              : rawFileName;

                          return (
                            <li
                              key={file.id || index}
                              className="branch-widget--uploaded-files__list-item"
                            >
                              <Card className="branch-widget--uploaded-files__card">
                                <Card.Body className="branch-widget--uploaded-files__body">
                                  <div className="branch-widget--uploaded-files__row">
                                    {/* Avatar/file type icon */}
                                    <div className="branch-widget--uploaded-files__avatar">
                                      <i
                                        className={`branch-widget--uploaded-files__icon ${fileIcon.icon}`}
                                        aria-hidden="true"
                                      ></i>
                                      <div className="branch-widget--uploaded-files__meta">
                                        {getFileTypeFromName(rawFileName)}
                                      </div>
                                    </div>
                                    {/* Main content */}
                                    <div className="branch-widget--uploaded-files__content">
                                      <div className="branch-widget--uploaded-files__header">
                                        <h3
                                          className="branch-widget--uploaded-files__title"
                                          title={displayFileName}
                                        >
                                          {displayFileName}
                                        </h3>

                                        <button
                                          type="button"
                                          className="branch-widget--uploaded-files__action"
                                          aria-label={`View file ${displayFileName}`}
                                          onClick={() => handleFileView(file)}
                                        >
                                          <i
                                            className="fas fa-search-plus"
                                            aria-hidden="true"
                                          ></i>
                                        </button>
                                      </div>

                                      <div className="branch-widget--uploaded-files__meta">
                                        <strong>Uploaded on:</strong>{" "}
                                        {formatFileDateTime(file.uploadedAt)}
                                      </div>

                                      <div className="branch-widget--uploaded-files__transaction"></div>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="col-12 text-center py-4">
                        <div className="text-muted">
                          <i
                            className="fas fa-folder-open"
                            style={{
                              fontSize: "3rem",
                              marginBottom: "1rem",
                              opacity: 0.3,
                            }}
                          ></i>
                          <h6>No documents found</h6>
                          <p>Upload your first document to get started.</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Table View */
                  <div style={{ overflowX: "auto" }}>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "0.5rem" }}
                      >
                        <span style={{ fontSize: 12 }}>Items per page:</span>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: "60px", fontSize: 12 }}
                          value={filePagination.pageSize}
                          onChange={(e) => {
                            const newPageSize = parseInt(e.target.value, 10);
                            setFilePagination((prev) => ({
                              ...prev,
                              pageSize: newPageSize,
                              currentPage: 0,
                            }));
                          }}
                        >
                          <option value={20}>20</option>
                          <option value={40}>40</option>
                        </select>
                        <span style={{ fontSize: 12, marginLeft: "1rem" }}>
                          1 - 20 of 1238 items
                        </span>
                      </div>
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "0.5rem", marginBottom: "5px" }}
                      >
                        <select
                          className="form-select form-select-sm"
                          style={{ width: "50px", fontSize: 12 }}
                          value={filePagination.currentPage + 1}
                          onChange={(e) => {
                            const newPage = parseInt(e.target.value, 10) - 1;
                            setFilePagination((prev) => ({
                              ...prev,
                              currentPage: newPage,
                            }));
                          }}
                        >
                          {Array.from(
                            {
                              length: Math.max(
                                1,
                                filePagination.totalPages || fileTotalPages || 1
                              ),
                            },
                            (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {i + 1}
                              </option>
                            )
                          )}
                        </select>
                        <span style={{ fontSize: 12 }}>
                          of {filePagination.totalPages || fileTotalPages || 40}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: 12,
                            minWidth: "32px",
                            height: "32px",
                          }}
                          disabled={filePagination.currentPage === 0}
                          onClick={() => {
                            if (filePagination.currentPage > 0) {
                              setFilePagination((prev) => ({
                                ...prev,
                                currentPage: prev.currentPage - 1,
                              }));
                              setFilePage(filePagination.currentPage - 1);
                            }
                          }}
                        >
                          <i className="fas fa-chevron-left"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: 12,
                            minWidth: "32px",
                            height: "32px",
                          }}
                          disabled={
                            filePagination.currentPage >=
                            (filePagination.totalPages || fileTotalPages || 1) -
                              1
                          }
                          onClick={() => {
                            if (
                              filePagination.currentPage <
                              (filePagination.totalPages ||
                                fileTotalPages ||
                                1) -
                                1
                            ) {
                              setFilePagination((prev) => ({
                                ...prev,
                                currentPage: prev.currentPage + 1,
                              }));
                              setFilePage(filePagination.currentPage + 1);
                            }
                          }}
                        >
                          <i className="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                    <Table
                      striped
                      hover
                      size="sm"
                      style={{ fontSize: 12, minWidth: "100%" }}
                    >
                      <thead>
                        <tr>
                          <FileSortableHeader
                            field="fileName"
                            label="FILE NAME"
                          />
                          <FileSortableHeader
                            field="fileType"
                            label="FILE TYPE"
                          />
                          <FileSortableHeader field="uploader" label="WHO" />
                          <FileSortableHeader
                            field="uploadedAt"
                            label="UPLOAD TIME/DATE"
                          />
                          <th style={{ width: "50px", textAlign: "center" }}>
                            <i className="fas fa-search-plus" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fileLoading ? (
                          <tr>
                            <td colSpan={5} className="text-center py-4">
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              Loading files...
                            </td>
                          </tr>
                        ) : files.length > 0 ? (
                          files.map((file, index) => (
                            <tr key={file.id || index}>
                              <td style={{ fontWeight: "500" }}>
                                {file.originalFileName || file.fileName}
                              </td>
                              <td style={{ textTransform: "uppercase" }}>
                                {getFileTypeFromName(
                                  file.originalFileName || file.fileName
                                )}
                              </td>
                              <td
                                style={{ fontSize: "11px", lineHeight: "1.3" }}
                              >
                                {file.uploadedByName}
                              </td>
                              <td>{formatFileDateTime(file.uploadedAt)}</td>
                              <td style={{ textAlign: "center" }}>
                                <i
                                  className="fas fa-search-plus"
                                  style={{
                                    color: "#6c757d",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                  }}
                                  title="View"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFileView(file);
                                  }}
                                ></i>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center py-4">
                              <div className="text-muted">
                                <i
                                  className="fas fa-folder-open"
                                  style={{
                                    fontSize: "2rem",
                                    marginBottom: "0.5rem",
                                    opacity: 0.3,
                                  }}
                                ></i>
                                <div>No documents found</div>
                                <small>
                                  Upload your first document to get started.
                                </small>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Document Upload Timeline */}
          <div className="col-md-4">
            <div
              className="card"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
              }}
            >
              <div
                className="card-header"
                style={{
                  backgroundColor: "#fff",
                  borderBottom: "1px solid #e9ecef",
                }}
              >
                <h6 className="mb-0">Document Upload Timeline</h6>
              </div>
              <div className="card-body p-3">
                {/* Timeline Items */}
                <div className="mb-3">
                  <div className="d-flex align-items-start">
                    <i
                      className="fas fa-file-pdf mr-2"
                      style={{ color: "#dc3545", marginTop: "2px" }}
                    ></i>
                    <div className="flex-grow-1">
                      <div className="fw-bold" style={{ fontSize: "13px" }}>
                        auto_loan.pdf
                      </div>
                      <div style={{ fontSize: "11px", color: "#666" }}>
                        <div>
                          <strong>FILE NAME:</strong> auto_loan
                        </div>
                        <div>
                          <strong>FILE TYPE:</strong> Pdf
                        </div>
                        <div>
                          <strong>WHO:</strong> Dawn, branch manager
                          <br />
                          Employee ID: 658970
                        </div>
                      </div>
                    </div>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      1 day ago
                    </small>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-start">
                    <i
                      className="fas fa-file-pdf mr-2"
                      style={{ color: "#dc3545", marginTop: "2px" }}
                    ></i>
                    <div className="flex-grow-1">
                      <div className="fw-bold" style={{ fontSize: "13px" }}>
                        file.pdf
                      </div>
                      <div style={{ fontSize: "11px", color: "#666" }}>
                        <div>
                          <strong>FILE NAME:</strong> file
                        </div>
                        <div>
                          <strong>FILE TYPE:</strong> Pdf
                        </div>
                        <div>
                          <strong>WHO:</strong> Ajay Hans
                        </div>
                      </div>
                    </div>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      1 week ago
                    </small>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-start">
                    <i
                      className="fas fa-file-pdf mr-2"
                      style={{ color: "#dc3545", marginTop: "2px" }}
                    ></i>
                    <div className="flex-grow-1">
                      <div className="fw-bold" style={{ fontSize: "13px" }}>
                        mortgage_agreement.pdf
                      </div>
                      <div style={{ fontSize: "11px", color: "#666" }}>
                        <div>
                          <strong>FILE NAME:</strong> mortgage_agreement
                        </div>
                        <div>
                          <strong>FILE TYPE:</strong> Pdf
                        </div>
                        <div>
                          <strong>WHO:</strong> Ajay Hans
                        </div>
                      </div>
                    </div>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      1 month ago
                    </small>
                  </div>
                </div>

                <div className="mb-0">
                  <div className="d-flex align-items-start">
                    <i
                      className="fas fa-file-pdf mr-2"
                      style={{ color: "#dc3545", marginTop: "2px" }}
                    ></i>
                    <div className="flex-grow-1">
                      <div className="fw-bold" style={{ fontSize: "13px" }}>
                        digital_KYC.pdf
                      </div>
                      <div style={{ fontSize: "11px", color: "#666" }}>
                        <div>
                          <strong>FILE NAME:</strong> digital_KYC
                        </div>
                        <div>
                          <strong>FILE TYPE:</strong> Pdf
                        </div>
                        <div>
                          <strong>WHO:</strong> INSTNT,
                          <br />
                          Digital ID Verification
                        </div>
                      </div>
                    </div>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      Feb. 03, 2025
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
